import { useState, useMemo } from 'react'
import { useProductStore } from '../store/productStore'
import { useCategoryStore } from '../store/categoryStore'
import type { Product } from '../types'

export type SortBy = 'default' | 'price_asc' | 'price_desc' | 'name' | 'qty_asc' | 'qty_desc'
export type StatusFilter = Product['status'] | 'all'
export type CategoryFilter = string // 'all' | root category id

export function useCatalogFilters() {
  const { products } = useProductStore()
  const { categories } = useCategoryStore()

  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all')
  const [search, setSearch]             = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [inStockOnly, setInStockOnly]   = useState(false)
  const [priceMin, setPriceMin]         = useState<number | ''>('')
  const [priceMax, setPriceMax]         = useState<number | ''>('')
  const [sortBy, setSortBy]             = useState<SortBy>('default')

  const [selectedShortcut, setSelectedShortcut] = useState<string | null>(null)

  // כל הקטגוריות הראשיות (ללא parent)
  const rootCategories = useMemo(() =>
    categories.filter(c => !c.parent_id).sort((a, b) => a.order - b.order),
    [categories]
  )

  // מיפוי category_id → root id
  const categoryToRoot = useMemo(() => {
    const map = new Map<string, string>()
    categories.forEach(c => {
      if (!c.parent_id) {
        map.set(c.id, c.id) // root מצביע על עצמו
      } else {
        map.set(c.id, c.parent_id) // תת-קטגוריה מצביעה על ה-root שלה
      }
    })
    return map
  }, [categories])

  const getProductRootId = (p: Product): string | null => {
    if (p.category_id) return categoryToRoot.get(p.category_id) ?? null
    return null
  }

  const filtered = useMemo(() => {
    let list = products
      .filter(p => selectedCategory === 'all' || getProductRootId(p) === selectedCategory)
      .filter(p => {
        if (!selectedShortcut) return true
        const shortcutRoot = categoryToRoot.get(selectedShortcut)
        if (shortcutRoot !== selectedCategory) return true
        return p.category_id === selectedShortcut
      })
      .filter(p => !search || p.name.includes(search) || p.description?.includes(search))
      .filter(p => statusFilter === 'all' || p.status === statusFilter)
      .filter(p => !inStockOnly || p.quantity > 0)
      .filter(p => priceMin === '' || p.price >= Number(priceMin))
      .filter(p => priceMax === '' || p.price <= Number(priceMax))
  
    if (sortBy === 'price_asc')  list = [...list].sort((a, b) => a.price - b.price)
    if (sortBy === 'price_desc') list = [...list].sort((a, b) => b.price - a.price)
    if (sortBy === 'name')       list = [...list].sort((a, b) => a.name.localeCompare(b.name, 'he'))
    if (sortBy === 'qty_asc')    list = [...list].sort((a, b) => a.quantity - b.quantity)
    if (sortBy === 'qty_desc')   list = [...list].sort((a, b) => b.quantity - a.quantity)
  
    return list
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, categories, selectedCategory, selectedShortcut, search, statusFilter, inStockOnly, priceMin, priceMax, sortBy])
  
  const activeFilterCount = [
    statusFilter !== 'all',
    inStockOnly,
    priceMin !== '',
    priceMax !== '',
    sortBy !== 'default',
  ].filter(Boolean).length

  const clearFilters = () => {
    setStatusFilter('all')
    setInStockOnly(false)
    setPriceMin('')
    setPriceMax('')
    setSortBy('default')
  }

  // counts דינמי לפי root categories
  const counts = useMemo(() => {
    const result: Record<string, number> = { all: products.length }
    rootCategories.forEach(root => {
      result[root.id] = products.filter(p => getProductRootId(p) === root.id).length
    })
    return result
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, categories, rootCategories])

  return {
    selectedCategory, setSelectedCategory,
    search, setSearch,
    statusFilter, setStatusFilter,
    inStockOnly, setInStockOnly,
    priceMin, setPriceMin,
    priceMax, setPriceMax,
    sortBy, setSortBy,
    filtered,
    activeFilterCount,
    counts,
    clearFilters,
    rootCategories, // Category[]
    selectedShortcut, 
    setSelectedShortcut
  }
}